import { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { motion } from 'motion/react'
import { Icon } from '@/components'
import BackButton from '@/components/BackButton/BackButton'
import Text from '@/components/Text'
import { Link } from '@mui/material'
import useZendeskArticle from './useZendeskArticle'
import './styles.scss'

export default function ExplainOptiwattPage() {
  const { programId } = useParams<{ programId: string }>()
  const history = useHistory()

  const { bodyHtml, isLoading, error } = useZendeskArticle()

  const OPTIWATT_FAQ_URL = 'https://optiwatt.com/faq'

  const [isExiting, setIsExiting] = useState(false)

  const navigateBack = () => {
    if (isExiting) return
    setIsExiting(true)
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 80 }}
      animate={isExiting ? { opacity: 0, x: -80 } : { opacity: 1, x: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      onAnimationComplete={() => {
        if (isExiting) {
          history.push(`/program/${programId}/signup`)
        }
      }}
      className="min-h-screen bg-white font-[inter]"
    >
      <div className="max-w-2xl mx-auto px-6 py-8 md:px-8 md:py-12">
        {/* Back Button */}
        <div className="mb-8">
          <BackButton onClick={navigateBack} />
        </div>

        {/* Header Section */}
        <div className="mb-10">
          <img
            src="/img/logo-color.webp"
            width={140}
            alt="Optiwatt"
            className="block mb-4"
          />

          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Icon name="Users" size={18} color="#7C3AED" />
              <span className="text-gray-600 text-sm font-medium">
                20,000+ people enrolled in programs
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="Star" size={18} color="#7C3AED" />
              <span className="text-gray-600 text-sm font-medium">
                4.8/5 rating on the{' '}
                <a
                  href="https://apps.apple.com/us/app/optiwatt-local-energy-rewards/id1536047033"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 !underline"
                >
                  App Store
                </a>
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-gray-200 my-8" />

        {/* Middle Content — Zendesk Article */}
        <div className="mb-10">
          {isLoading && (
            <div className="flex flex-col gap-4 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-100 rounded w-full" />
              <div className="h-4 bg-gray-100 rounded w-full" />
              <div className="h-4 bg-gray-100 rounded w-5/6" />
              <div className="h-4 bg-gray-100 rounded w-full" />
              <div className="h-4 bg-gray-100 rounded w-2/3" />
            </div>
          )}
          {error && (
            <div className="text-center py-8">
              <Text variant="body2" className="text-gray-500">
                Unable to load content. Please try again later.
              </Text>
            </div>
          )}
          {bodyHtml && (
            <div
              className="zendesk-article-content"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          )}
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-gray-200 my-8" />

        {/* Footer Section */}
        <div className="flex flex-col items-center gap-4 pb-8">
          <p className="text-gray-600 text-sm font-medium m-0">
            Still have questions?
          </p>
          <Link
            href={OPTIWATT_FAQ_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="!text-[#7C3AED] !font-bold !text-sm !underline flex items-center gap-1"
          >
            Visit FAQ Page
            <Icon name="ExternalLink" size={14} color="#7C3AED" />
          </Link>

          <button
            onClick={navigateBack}
            className="w-full max-w-sm bg-[#af4bfb] hover:bg-[#9a3ae6] text-white font-semibold text-base py-4 rounded-[20px] transition-colors border-0 cursor-pointer mt-4"
          >
            Return to Sign Up
          </button>
        </div>
      </div>
    </motion.div>
  )
}
